/**
 * The filter well — `docs/list-filters.md` §4.4, drawn from
 * `design/mockups/30-list-filters/html/filters-04-card-builder.html`.
 *
 * A card is a filter with a number on it, so "Which records" should look like
 * what it is: the conditions as chips you can take off, and a dashed chip that
 * opens the same quick popover the toolbar opens. What it replaces was a
 * read-only box saying "Where status is completed and …" with two links under
 * it — a sentence about a filter rather than the filter, and no way to drop
 * one rule without opening the builder and finding it.
 *
 * It is deliberately the *same* popover, not a second one shaped like it: a
 * card's records are rows of this list, so the catalogue, the controls and the
 * counts all come from the list it was opened from. Only where the answer is
 * written differs, which is what `QuickFilter`'s `value`/`onChange` is for.
 */
import { useRef, useState, type ReactNode } from 'react'
import {
  describeFilter, describeRelated, isFilterGroup, isRelatedCondition,
  type FilterCondition, type FilterGroup, type FilterNode,
} from '@rowted/shared'
import { QuickFilter } from './quick-filter.js'
import { summaryOf } from './filter-kind-controls.js'
import { filterLabels } from './list-filter.js'
import { IconClose, IconPlus } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import type { UseListResult } from '../lib/use-list.js'

export interface FilterWellProps {
  list: UseListResult
  /** The filter being edited — a card's, not the list's. */
  value: FilterGroup | undefined
  /** Given `undefined` when the last chip comes off. */
  onChange: (next: FilterGroup | undefined) => void
  /** Opens the advanced builder on whatever is in the well now. */
  onAdvanced: () => void
}

/**
 * What one chip says. A plain condition is its column and its answer — the
 * same words `summaryOf` puts on the quick filter's section headers, so a
 * chip and the section it came from never describe the same rule differently.
 * A group or a related condition has no single column, so it says the whole
 * of itself.
 */
export function chipLabel(node: FilterNode, list: UseListResult): string {
  const labels = filterLabels(list)
  if (isRelatedCondition(node)) return describeRelated(node, labels)
  if (isFilterGroup(node)) return describeFilter(node, labels, true)

  const condition = node as FilterCondition
  const field = list.fields.find((candidate) => candidate.name === condition.field)
  if (!field) return describeFilter({ match: 'and', conditions: [condition] }, labels)
  return `${field.label} · ${summaryOf(field, condition, list.labelCache)}`
}

export function FilterWell({ list, value, onChange, onAdvanced }: FilterWellProps) {
  const [adding, setAdding] = useState(false)
  const addChip = useRef<HTMLButtonElement>(null)

  const nodes = value?.conditions ?? []

  const listFilter = list.view.filter
  const listCount = listFilter?.conditions.length ?? 0

  const without = (index: number) => {
    const rest = nodes.filter((_, at) => at !== index)
    onChange(rest.length > 0 ? { match: value?.match ?? 'and', conditions: rest } : undefined)
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={cn(
          'flex min-h-[40px] flex-wrap items-center gap-1.5 rounded-field border border-line',
          'bg-surface p-1.5 focus-within:ring-2 focus-within:ring-accent-ring',
        )}
      >
        {nodes.map((node, index) => (
          <span
            key={index}
            className={cn(
              'inline-flex h-7 items-center gap-1 rounded-chip border border-accent-line',
              'bg-accent-soft py-0 pl-2.5 pr-2 text-chip text-accent-dark',
            )}
          >
            <span className="max-w-[240px] truncate">{chipLabel(node, list)}</span>
            <button
              type="button"
              onClick={() => without(index)}
              aria-label={`Remove ${chipLabel(node, list)}`}
              className="text-accent-dark hover:text-danger"
            >
              <IconClose size={11} />
            </button>
          </span>
        ))}

        <button
          ref={addChip}
          type="button"
          onClick={() => setAdding(true)}
          className={cn(
            'inline-flex h-7 items-center gap-1 rounded-chip border border-dashed',
            'border-line-strong px-2.5 text-chip font-semibold text-accent',
            'hover:border-accent-line hover:bg-accent-soft',
          )}
        >
          <IconPlus size={12} />
          Add a filter
        </button>
      </div>

      <p className="m-0 text-sub leading-snug text-text-faint">
        The same filter as the list — quick chips here, Advanced… for groups.
      </p>

      <div className="flex items-center gap-3.5">
        <WellLink
          onClick={() => onChange(listFilter)}
          disabled={listCount === 0}
        >
          Use this list’s filter{listCount > 0 ? ` · ${listCount} ${listCount === 1 ? 'rule' : 'rules'}` : ''}
        </WellLink>
        <WellLink onClick={onAdvanced}>Advanced…</WellLink>
      </div>

      <QuickFilter
        list={list}
        open={adding}
        anchor={addChip.current}
        onClose={() => setAdding(false)}
        value={value ?? { match: 'and', conditions: [] }}
        onChange={(next) => onChange(next.conditions.length > 0 ? next : undefined)}
        onAdvanced={() => { setAdding(false); onAdvanced() }}
      />
    </div>
  )
}

function WellLink({
  onClick, disabled, children,
}: { onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'text-sub font-semibold',
        disabled ? 'cursor-default text-text-faint' : 'text-accent hover:text-accent-dark',
      )}
    >
      {children}
    </button>
  )
}
